import { APP_INITIALIZER, ApplicationConfig, importProvidersFrom } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { provideRouter, Router } from '@angular/router';
import { appRoutes } from './app.routes';
import { HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { RECAPTCHA_LOADER_OPTIONS } from 'ng-recaptcha';
import { APP_CONFIG, AppConfig } from '../configuration';
import { SupportedLocale } from '../consts/locale';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { FeatureFlagsService, AngularHttpAdapter, FF_SERVICE } from '@portal-front/feature-flags/angular';
import { createFeatureFlagsConfig } from '@portal-front/feature-flags/core';
import { AUTH_CONTEXT_CONFIG, AuthContextConfig } from '@portal-front/portal-auth-shared-util-config';
import { captureException } from '@portal-front/shared-util-sentry';
import { registerChunkLoadErrorRecovery } from './chunk-load-error-handler';

import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { CookieService } from 'ngx-cookie-service';
import { DYNAMIC_LOCALE } from '@portal-front/util-locale';
import { LanguageService } from '@portal-front/util-locale';
import { firstValueFrom } from 'rxjs';

export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http, './assets/locales/messages.', '.json');
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(appRoutes),
    provideHttpClient(withInterceptorsFromDi()),
    provideAnimationsAsync(),
    CookieService,
    {
      provide: FF_SERVICE,
      useFactory: (config: AppConfig) =>
        createFeatureFlagsConfig({
          app: 'portal-front',
          apiUrl: config.apiUrl,
          timeout: 5000,
          onError: captureException,
        }),
      deps: [APP_CONFIG],
    },
    AngularHttpAdapter,
    FeatureFlagsService,
    {
      provide: DYNAMIC_LOCALE,
      useFactory: (languageService: LanguageService): string => {
        return languageService.getLanguageToUse();
      },
      deps: [LanguageService],
    },
    importProvidersFrom(
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: HttpLoaderFactory,
          deps: [HttpClient],
        },
        defaultLanguage: 'pl',
      }),
    ),
    {
      provide: AUTH_CONTEXT_CONFIG,
      useFactory: (
        config: AppConfig,
        locale: SupportedLocale,
        featureFlagsService: FeatureFlagsService,
      ): AuthContextConfig => {
        const isTurnstileEnabled = featureFlagsService.isFeatureEnabled('ff-enable-register-cf-turnstile');
        const captchaProvider = isTurnstileEnabled ? 'turnstile' : 'recaptcha';

        return {
          captchaSiteKey: config.googleCaptcha.siteKey,
          turnstileSiteKey: config.turnstileCaptcha?.siteKey,
          captchaProvider,
          registrationUrl: config.urls[locale].registrationUrl,
          userAccountUrl: config.urls[locale].userAccountUrl,
          googleAuthClientId: config.googleAuth.clientId,
          termsUrl: config.urls[locale].termsUrl,
          privacyPolicyUrl: config.urls[locale].privacyPolicyUrl,
          featureFlags: config.featureFlags,
        };
      },
      deps: [APP_CONFIG, DYNAMIC_LOCALE, FeatureFlagsService],
    },
    {
      provide: RECAPTCHA_LOADER_OPTIONS,
      useFactory: (locale: string) => ({
        onBeforeLoad(url: URL) {
          url.searchParams.set('hl', locale);

          return { url };
        },
      }),
      deps: [DYNAMIC_LOCALE],
    },
    {
      provide: 'googleTagManagerId',
      useFactory: (config: AppConfig, locale: string) => {
        return config.gtm[locale as SupportedLocale];
      },
      deps: [APP_CONFIG, DYNAMIC_LOCALE],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: (languageService: LanguageService, document: Document): (() => void) => {
        return (): void => {
          languageService.initializeLanguage();
          document.documentElement.lang = languageService.getLanguageToUse();
        };
      },
      deps: [LanguageService, DOCUMENT],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: (featureFlagsService: FeatureFlagsService) => {
        return () => firstValueFrom(featureFlagsService.loadFeatureFlags());
      },
      deps: [FeatureFlagsService],
      multi: true,
    },
    {
      // Odzyskiwanie po błędach lazy-loadingu chunków (np. po deployu – stary index.html w cache
      // odwołuje się do hashy chunków, których już nie ma na serwerze → biała strona).
      provide: APP_INITIALIZER,
      useFactory: (router: Router) => {
        return () => registerChunkLoadErrorRecovery(router);
      },
      deps: [Router],
      multi: true,
    },
  ],
};
